/**
 * Name utility functions
 */

/**
 * Extracts initials from a full name
 * @param name - Full name string
 * @returns Initials (2 characters for first and last name, or first 2 chars of single name)
 * @example
 * getInitials('John Doe') // Returns 'JD'
 * getInitials('Madonna') // Returns 'MA'
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return '';
  }

  const parts = name.trim().split(' ');

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return parts[0]?.substring(0, 2).toUpperCase() || '';
};

/**
 * Formats a full name to Title Case
 * @param name - Full name string
 * @returns Name in Title Case
 * @example
 * formatFullName('john doe') // Returns 'John Doe'
 */
export const formatFullName = (name: string): string => {
  if (!name) return '';

  return name
    .trim()
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Gets first name from full name
 * @param name - Full name string
 * @returns First name
 * @example
 * getFirstName('John Doe') // Returns 'John'
 */
export const getFirstName = (name: string): string => {
  if (!name) return '';
  return name.trim().split(' ')[0] || '';
};

/**
 * Gets last name from full name
 * @param name - Full name string
 * @returns Last name
 * @example
 * getLastName('John Doe') // Returns 'Doe'
 */
export const getLastName = (name: string): string => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : '';
};
